import { api } from './client'

// ============================================================================
// Lô sản phẩm — pattern PNK + PXK với items M-N (Phase product-lots-redesign)
// Status sống trên phiếu: pending|approved|rejected|cancelled
// ============================================================================

export type NoteStatus = 'pending' | 'approved' | 'rejected' | 'cancelled'

export type ProductLotInReason =
    | 'from_packaging'
    | 'from_customer_return'
    | 'from_order_cancel'
    | 'adjustment'
    | 'from_manufacturing_partner'
export type ProductLotOutReason = 'for_sale' | 'discard' | 'adjustment'

export interface ProductLot {
    id: string
    code: string
    product_id: string
    product_name?: string
    product_sku?: string
    product_unit?: string
    warehouse_id: string
    warehouse_name?: string
    position_id?: string | null
    position_code?: string | null
    position_name?: string | null
    product_image_url?: string | null
    manufactured_date?: string | null
    expiry_date?: string | null
    quantity_in: number
    quantity_available: number
    /** Mức CK đánh dấu của lô (0 = không đánh dấu). Ưu tiên chọn lô khi đơn CK khớp. */
    discount_percent: number
    notes?: string | null
    created_by_id?: string | null
    created_by_name?: string
    created_at: string
    updated_at: string
}

export interface ProductLotStockInNoteItem {
    id: string
    note_id: string
    product_lot_id: string
    product_lot_code?: string
    product_name?: string
    product_unit?: string
    position_code?: string | null
    manufactured_date?: string | null
    expiry_date?: string | null
    product_image_url?: string | null
    quantity: number
    created_at: string
    /** Nguồn tầng dòng (reason=from_packaging): danh sách lô lấy hương nguồn + kg. */
    sources?: ProductLotStockInNoteItemSource[]
    /** Vật tư đóng gói của dòng này (chặng sản phẩm, tuỳ chọn). */
    materials?: ProductLotStockInNoteItemMaterial[]
}

/** 1 lô lấy hương nguồn của 1 dòng lô sản phẩm (đọc từ API). */
export interface ProductLotStockInNoteItemSource {
    id: string
    note_item_id: string
    source_scenting_lot_id: string
    source_quantity: number
    source_scenting_lot_code?: string
}

/** 1 lô vật tư + số lượng khai trên 1 dòng lô sản phẩm (đọc từ API). */
export interface ProductLotStockInNoteItemMaterial {
    id: string
    note_item_id: string
    packaging_lot_id: string
    quantity: number
    packaging_lot_code?: string
    material_name?: string
}

export interface ProductLotStockInNote {
    id: string
    code: string
    status: NoteStatus
    reason: ProductLotInReason
    notes?: string | null
    created_by_id?: string | null
    created_by_name?: string
    approved_by_id?: string | null
    approved_by_name?: string
    approved_at?: string | null
    rejected_reason?: string | null
    cancelled_at?: string | null
    sales_order_id?: string | null
    order_code?: string
    stocktake_session_id?: string | null
    stocktake_session_code?: string
    scenting_lot_stock_out_note_id?: string | null
    scenting_out_note_code?: string
    packaging_lot_stock_out_note_id?: string | null
    packaging_out_note_code?: string
    /** Phiếu lấy hàng kho lấy hương đã chọn làm gợi ý (chỉ ghi lại, cho sửa). */
    scenting_lot_withdrawal_note_id?: string | null
    /** Phiếu lấy hàng kho vật tư đã chọn làm gợi ý (ghi lại, cho sửa). */
    packaging_lot_withdrawal_note_id?: string | null
    created_at: string
    updated_at: string
    items_count?: number
    items?: ProductLotStockInNoteItem[]
}

export type ProductLotStockOutNoteItem = ProductLotStockInNoteItem

export interface ProductLotStockOutNote extends Omit<
    ProductLotStockInNote,
    'reason' | 'items'
> {
    reason: ProductLotOutReason
    items?: ProductLotStockOutNoteItem[]
}

export interface ProductLotHistoryEntry {
    note_id: string
    note_code: string
    note_type: 'in' | 'out'
    status: NoteStatus
    reason: string
    quantity: number
    notes?: string | null
    created_at: string
    approved_at?: string | null
    created_by_name?: string
}

// ============================================================================
// Request shapes
// ============================================================================

/** 1 lô vật tư + số lượng gửi lên khi tạo/sửa PNK. */
export interface ItemMaterialInput {
    packaging_lot_id: string
    quantity: number
}

/** 1 lô lấy hương nguồn gửi lên khi tạo/sửa PNK. */
export interface ItemSourceInput {
    source_lot_id: string
    source_quantity: number
}

/** Nguồn tầng dòng — chỉ gửi khi reason=from_packaging. */
interface ItemSource {
    /** Danh sách nhiều lô lấy hương nguồn. */
    sources?: ItemSourceInput[]
    /** Vật tư đóng gói của dòng (tuỳ chọn). */
    materials?: ItemMaterialInput[]
}

export type CreateInNoteItem =
    | ({ product_lot_id: string; quantity: number } & ItemSource)
    | ({
          new_lot_product_id: string
          new_lot_warehouse_id?: string
          new_lot_position_id: string
          new_lot_code?: string
          new_lot_manufactured_date?: string
          new_lot_expiry_date?: string
          /** Mức CK đánh dấu cho lô mới (0 = không đánh dấu). */
          new_lot_discount_percent?: number
          quantity: number
      } & ItemSource)

export interface CreateInNoteRequest {
    reason: ProductLotInReason
    notes?: string
    items: CreateInNoteItem[]
    /**
     * Tuỳ chọn — PXK vật tư (không phải SP nào cũng tốn vật tư đóng gói).
     * (PXK lô lấy hương giờ tự sinh lúc duyệt từ nguồn tầng dòng — không gửi.)
     */
    packaging_lot_stock_out_note_id?: string
    /** Phiếu lấy hàng kho lấy hương làm gợi ý (được lưu + trừ số dư khi tạo). */
    source_withdrawal_note_id?: string
    /** Phiếu lấy hàng kho vật tư làm gợi ý (được lưu + trừ số dư khi tạo). */
    packaging_withdrawal_note_id?: string
}

export interface CreateOutNoteItemInput {
    product_lot_id: string
    quantity: number
}

export interface CreateOutNoteRequest {
    reason: ProductLotOutReason
    notes?: string
    items: CreateOutNoteItemInput[]
}

// Sửa PNK/PXK pending: reason bất biến nhưng payload giống lúc tạo.
export type UpdateInNoteRequest = CreateInNoteRequest
export type UpdateOutNoteRequest = CreateOutNoteRequest

export interface ListLotsParams {
    product_id?: string
    warehouse_id?: string
    position_id?: string
    search?: string
    only_available?: '1' | undefined
    page?: number
    per_page?: number
}

export interface ListNotesParams {
    status?: NoteStatus
    reason?: string
    sales_order_id?: string
    date_from?: string
    date_to?: string
    page?: number
    per_page?: number
}

// ============================================================================
// Lineage — truy xuất nguồn gốc 5 hop
// ============================================================================

export type LineageReason =
    | 'upstream_broken'
    | 'packaging_pnk_reason_not_from_process'
    | 'packaging_sources_missing'
    | 'scenting_process_not_found'
    | 'scenting_sources_missing'
    | 'sorting_sources_missing'
    | 'grower_missing'

export interface LineageHop<T> {
    available: boolean
    reason?: LineageReason
    data?: T
}

export interface ScentingLotSummary {
    id: string
    code: string
    aroma_material_name?: string
    warehouse_name?: string
    created_at: string
}

export interface SortingLotSummary {
    id: string
    code: string
    tea_type_name?: string
    grade?: string
    warehouse_name?: string
    created_at: string
}

export interface PurchaseLotSummary {
    id: string
    code: string
    quantity_in: number
    created_at: string
}

export interface TeaPurchaseSummary {
    id: string
    code: string
    purchase_date: string
    tea_type_name?: string
    quantity: number
}

export interface GrowerSummary {
    id: string
    name: string
    phone?: string
    address?: string
}

export interface ProductLotLineage {
    product_lot: {
        id: string
        code: string
        product_id: string
        product_sku: string
        product_name: string
        manufactured_date?: string
        expiry_date?: string
    }
    scenting_lots: LineageHop<ScentingLotSummary[]>
    sorting_lots: LineageHop<SortingLotSummary[]>
    // 3 hop cuối là mảng vì multi-lot PXK cho sorting process có thể trace
    // ngược ra N nguồn khác nhau. Khi chỉ 1 nguồn, mảng có đúng 1 phần tử.
    purchase_lots: LineageHop<PurchaseLotSummary[]>
    tea_purchases: LineageHop<TeaPurchaseSummary[]>
    growers: LineageHop<GrowerSummary[]>
}

// ============================================================================
// API endpoints
// ============================================================================

const BASE = '/product-lots'

export const productLotApi = {
    // Lots
    listLots: (params?: ListLotsParams) =>
        api.get<ProductLot[]>(BASE, { params }),
    getLot: (id: string) => api.get<ProductLot>(`${BASE}/${id}`),
    getLotHistory: (
        id: string,
        params?: { page?: number; per_page?: number },
    ) => api.get<ProductLotHistoryEntry[]>(`${BASE}/${id}/history`, { params }),
    getLineage: (id: string) =>
        api.get<ProductLotLineage>(`${BASE}/${id}/lineage`),
    // Sửa mức CK đánh dấu của lô (sửa sau khi tạo).
    updateLotDiscount: (id: string, discountPercent: number) =>
        api.patch<{ id: string; discount_percent: number }>(
            `${BASE}/${id}/discount`,
            { discount_percent: discountPercent },
        ),
    // Sửa vị trí kho của lô.
    updateLotPosition: (id: string, positionId: string) =>
        api.patch<{ id: string; position_id: string }>(
            `${BASE}/${id}/position`,
            { position_id: positionId },
        ),

    // Stock-in notes
    listInNotes: (params?: ListNotesParams) =>
        api.get<ProductLotStockInNote[]>(`${BASE}/stock-in-notes`, { params }),
    getInNote: (id: string) =>
        api.get<ProductLotStockInNote>(`${BASE}/stock-in-notes/${id}`),
    createInNote: (req: CreateInNoteRequest) =>
        api.post<ProductLotStockInNote>(`${BASE}/stock-in-notes`, req),
    updateInNote: (id: string, req: UpdateInNoteRequest) =>
        api.put<ProductLotStockInNote>(`${BASE}/stock-in-notes/${id}`, req),
    approveInNote: (id: string) =>
        api.post<void>(`${BASE}/stock-in-notes/${id}/approve`),
    rejectInNote: (id: string, reason: string) =>
        api.post<void>(`${BASE}/stock-in-notes/${id}/reject`, { reason }),
    cancelInNote: (id: string) =>
        api.post<void>(`${BASE}/stock-in-notes/${id}/cancel`),

    // Stock-out notes
    listOutNotes: (params?: ListNotesParams) =>
        api.get<ProductLotStockOutNote[]>(`${BASE}/stock-out-notes`, {
            params,
        }),
    getOutNote: (id: string) =>
        api.get<ProductLotStockOutNote>(`${BASE}/stock-out-notes/${id}`),
    createOutNote: (req: CreateOutNoteRequest) =>
        api.post<ProductLotStockOutNote>(`${BASE}/stock-out-notes`, req),
    updateOutNote: (id: string, req: UpdateOutNoteRequest) =>
        api.put<ProductLotStockOutNote>(`${BASE}/stock-out-notes/${id}`, req),
    approveOutNote: (id: string) =>
        api.post<void>(`${BASE}/stock-out-notes/${id}/approve`),
    rejectOutNote: (id: string, reason: string) =>
        api.post<void>(`${BASE}/stock-out-notes/${id}/reject`, { reason }),
    cancelOutNote: (id: string) =>
        api.post<void>(`${BASE}/stock-out-notes/${id}/cancel`),
}
